import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchAlly } from '../services/alliesService';
import { byDisplayOrder } from '../utils/sortByDisplayOrder';
import AllyProductCard from './AllyProductCard';
import './AllyProductsCarousel.css';

/**
 * Home page pilot: a horizontal scroll-snap strip showcasing one featured
 * ally's catalog, to give the allies section more visibility on the storefront.
 *
 * `allySlug` is hardcoded per-ally for now (no "featured ally" flag in the
 * DB yet) — swapping the spotlighted ally means changing this prop.
 */
const AllyProductsCarousel = ({ allySlug = 'el-tesoro-del-juguete' }) => {
  const { t } = useTranslation('home');
  const [ally, setAlly] = useState(null);
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const trackRef = useRef(null);
  const [scrollPct, setScrollPct] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Reset before the new fetch resolves, so a failed refetch after
    // allySlug changes doesn't leave the previous ally's data on screen
    // under the new slug's link/title.
    setAlly(null);
    setProducts([]);
    setLoaded(false);
    const load = async () => {
      try {
        const data = await fetchAlly(allySlug);
        if (cancelled) return;
        setAlly(data.ally);
        setProducts(data.products || []);
      } catch (err) {
        // Non-fatal — this is a promotional section, not core content, so
        // the home page renders without it rather than breaking. Still log
        // so a broken feed (bad slug, API outage, deactivated ally) doesn't
        // go unnoticed.
        if (!cancelled) console.error('[AllyProductsCarousel] failed to load ally', allySlug, err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [allySlug]);

  const sortedProducts = useMemo(() =>
    [...products].sort(byDisplayOrder),
    [products]
  );

  // A slim progress bar rather than one-dot-per-product: ally catalogs can
  // run into the dozens of items, where per-card dots stop being legible.
  const handleScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollPct(max <= 0 ? 0 : el.scrollLeft / max);
  }, []);

  if (!loaded || !ally || sortedProducts.length === 0) return null;

  return (
    <section className="ally-carousel-section">
      <div className="container">
        <div className="ally-carousel-header">
          <Link to={`/ally/${ally.slug || ally.id}`} className="ally-carousel-title">
            {ally.name}
          </Link>
          <Link to={`/ally/${ally.slug || ally.id}`} className="ally-carousel-cta">
            {t('allyCarousel.viewCatalog')}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>

        <div className="ally-carousel-track" ref={trackRef} onScroll={handleScroll}>
          {sortedProducts.map((product) => (
            <div className="ally-carousel-card" key={product.id}>
              <AllyProductCard product={product} />
            </div>
          ))}
        </div>

        {sortedProducts.length > 1 && (
          <div className="ally-carousel-progress" aria-hidden="true">
            <div
              className="ally-carousel-progress-fill"
              style={{ transform: `scaleX(${Math.max(0.08, scrollPct)})` }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default AllyProductsCarousel;
